<script lang="ts">
  import { hasCommandModifier, isEditableTarget } from '../components/keyboard';
  import type { ProblemDoc } from '../components/problem/types';
  import { techniqueLabel } from '../lib/cards';
  import { exerciseTechniques, sourceLabel } from '../lib/problems';
  import { problems } from '../services';

  /**
   * ホーム = 問題一覧（docs/decisions/problem-first.md、problem-format.md §7、layout-and-navigation.md §3）。
   * 例題を order 順（手法の階段）に並べ、各例題の下に類題をぶら下げる。出典・手法を添える。
   * difficulty は内部値なので出さない（problem-format.md §2.2）。draft は services の索引が除く。
   * その下に、詰まったときに開く参照（レッスン一覧・ラボ・メーター）を並べる。
   */
  interface Props {
    onnavigate: (hash: string) => void;
    /** 一覧に出す問題（order 順。既定はアプリの索引） */
    list?: readonly ProblemDoc[];
  }

  let { onnavigate, list = problems.problems }: Props = $props();

  const references = [
    { href: '#/lessons', title: 'レッスン一覧', sub: '文法とデータ構造の読み物' },
    { href: '#/lab', title: 'ラボ', sub: 'スタック・キュー・辞書を触る' },
    { href: '#/meter', title: '計算量メーター', sub: '回数と実行時間の見積もり' },
  ] as const;

  function onkeydown(e: KeyboardEvent) {
    if (e.defaultPrevented || hasCommandModifier(e) || isEditableTarget(e.target)) return;
    if (e.key === 'l' || e.key === 'L') {
      e.preventDefault();
      onnavigate('#/lab');
    } else if (e.key === 'm' || e.key === 'M') {
      e.preventDefault();
      onnavigate('#/meter');
    } else if (e.key === ',') {
      e.preventDefault();
      onnavigate('#/settings');
    }
  }
</script>

<svelte:window {onkeydown} />

<div class="page">
  <div class="page-head">
    <h1 tabindex="-1">問題</h1>
  </div>

  <section class="panel" aria-labelledby="p-title" data-testid="problem-list">
    <h2 id="p-title" class="visually-hidden">問題一覧</h2>
    {#if list.length === 0}
      <p class="text-soft" data-testid="problems-pending">問題は準備中です。</p>
    {:else}
      <ol class="problems">
        {#each list as p (p.meta.id)}
          <li class="problem" data-testid="problem-item" data-problem-id={p.meta.id}>
            <a class="problem-link" href="#/problem/{p.meta.id}" data-testid="problem-link">
              <span class="src">{sourceLabel(p.meta.source)}</span>
              <strong class="title">{p.meta.title}</strong>
            </a>
            <ul class="tags" aria-label="手法">
              {#each p.meta.techniques as t (t)}<li class="tag">{techniqueLabel(t)}</li>{/each}
            </ul>
            {#if p.meta.exercises.length > 0}
              <ul class="exercises" aria-label="{p.meta.title} の類題">
                {#each p.meta.exercises as ex (ex.id)}
                  <li data-testid="exercise-item" data-exercise={ex.id}>
                    <a href="#/problem/{p.meta.id}" class="ex-link">
                      <span class="eyebrow">類題</span>
                      <span class="src">{sourceLabel(ex.source)}</span>
                      <span>{ex.title}</span>
                    </a>
                    <ul class="tags" aria-label="手法">
                      {#each exerciseTechniques(p.meta, ex) as t (t)}<li class="tag">
                          {techniqueLabel(t)}
                        </li>{/each}
                    </ul>
                  </li>
                {/each}
              </ul>
            {/if}
          </li>
        {/each}
      </ol>
    {/if}
  </section>

  <section class="section" aria-labelledby="r-title" data-testid="references">
    <h2 id="r-title" class="ref-title">参照</h2>
    <div class="tiles">
      {#each references as r (r.href)}
        <a class="tile" href={r.href}><strong>{r.title}</strong><span>{r.sub}</span></a>
      {/each}
    </div>
  </section>
</div>

<style>
  .page-head {
    margin-bottom: var(--space-5);
  }
  .problems {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
  }
  .problem {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-4) 0;
    min-width: 0;
  }
  .problem + .problem {
    border-top: 1px solid var(--border);
  }
  .problem-link {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--space-1) var(--space-3);
    font-size: var(--fs-lg);
    min-height: var(--tap-min);
  }
  .src {
    font-size: var(--fs-sm);
    color: var(--text-soft);
    font-weight: var(--fw-semibold);
  }
  .tags {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .tag {
    font-size: var(--fs-xs);
    padding: 0 var(--space-2);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-xs);
    color: var(--text);
  }
  .exercises {
    list-style: none;
    margin: var(--space-1) 0 0;
    padding: 0 0 0 var(--space-4);
    border-left: 2px solid var(--border);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .exercises > li {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-1) var(--space-3);
  }
  .ex-link {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--space-1) var(--space-2);
    min-height: var(--tap-min);
  }
  .section {
    margin-top: var(--space-6);
  }
  .ref-title {
    margin-bottom: var(--space-3);
  }
  .tiles {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr));
    gap: var(--space-3);
  }
  .tile {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    padding: var(--space-3) var(--space-4);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-md);
    background: var(--surface);
    color: var(--text);
  }
  .tile:hover {
    text-decoration: none;
    background: var(--canvas);
  }
  .tile strong {
    font-size: var(--fs-md);
  }
  .tile span {
    font-size: var(--fs-sm);
    color: var(--text-soft);
  }
</style>
